import { lintDataJson, lintErrors } from './dataJsonLint'

const NO_VALUE_TYPES = new Set(['button', 'label', 'table'])

function norm(value) {
  return String(value == null ? '' : value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/gi, '')
    .toLowerCase()
}

function asArray(value) {
  return Array.isArray(value) ? value : []
}

function fieldAliases(field = {}) {
  return [field.campoForm, field.tag, field.id, field.binding, field.variable, field.label]
    .map(norm)
    .filter(Boolean)
}

function fieldsOf(data = {}) {
  return asArray(data.forms).flatMap((form) => asArray(form && (form.remarks || form.fields)))
}

function valueFieldsOf(data = {}) {
  return fieldsOf(data).filter((field) => !NO_VALUE_TYPES.has(String(field && (field.typedata || field.type) || '').toLowerCase()))
}

function interpretationFieldsOf(ir = {}) {
  const output = []
  const add = (items) => asArray(items).forEach((field) => {
    if (!field || typeof field !== 'object') return
    if (!NO_VALUE_TYPES.has(String(field.typedata || field.type || '').toLowerCase())) output.push(field)
    if (field.type === 'field_row') add(field.parts)
    if (field.type === 'checkbox-group') add(field.options)
  })
  add(ir.fields)
  asArray(ir.forms).forEach((form) => add(form && (form.fields || form.remarks)))
  asArray(ir.steps).forEach((step) => add(step && step.fields))
  asArray(ir.subflows).forEach((subflow) => {
    add(subflow && subflow.fields)
    asArray(subflow && subflow.steps).forEach((step) => add(step && step.fields))
  })
  return output
}

function canonicalCommand(value, block) {
  let command = String(value == null ? '' : value).trim()
  if (!command) return ''
  if (block !== undefined && block !== null && block !== '' && !/^\d/.test(command)) {
    command = String(block) + command
  }
  return command
    .replace(/\{ENTER\}/gi, '')
    .replace(/\[@?[A-Za-z_][A-Za-z0-9_.]*\]/g, '{}')
    .replace(/\{\{?@?[A-Za-z_][A-Za-z0-9_.]*\}?\}/g, '{}')
    .replace(/\s+/g, '')
    .toUpperCase()
}

function commandValues(value, output) {
  if (value == null) return
  if (typeof value === 'string') {
    const command = canonicalCommand(value)
    if (command) output.add(command)
    return
  }
  if (Array.isArray(value)) {
    value.forEach((item) => commandValues(item, output))
    return
  }
  if (typeof value !== 'object') return
  const commandKeys = ['command', 'command_template', 'template', 'remark', 'remark_mask', 'dato']
  let found = false
  commandKeys.forEach((key) => {
    if (value[key] == null) return
    found = true
    const command = canonicalCommand(value[key], value.block)
    if (command) output.add(command)
  })
  if (!found) {
    ;['commands', 'then', 'else', 'steps'].forEach((key) => commandValues(value[key], output))
  }
}

function commandsOf(data = {}, logicJson = null) {
  const output = new Set()
  valueFieldsOf(data).forEach((field) => {
    ;['remark', 'remark_mask', 'dato'].forEach((key) => {
      const command = canonicalCommand(field && field[key], field && field.block)
      if (command) output.add(command)
    })
  })
  commandValues(data.postCommands, output)
  commandValues(data.onLoad, output)
  commandValues(data.actions, output)
  commandValues(data.command_plan, output)
  commandValues(data.source_additional && data.source_additional.command_plan, output)
  asArray(data.forms).forEach((form) => {
    commandValues(form && form.source_additional && form.source_additional.after_form_commands, output)
    commandValues(form && form.source_additional && form.source_additional.commands, output)
  })
  asArray(data.subflows).forEach((subflow) => {
    commandValues(subflow && subflow.command_plan, output)
    commandValues(subflow && subflow.steps, output)
  })
  Object.values((logicJson && logicJson.blocks) || {}).forEach((block) => commandValues(block, output))
  commandValues(logicJson && logicJson.onLoad, output)
  commandValues(logicJson && logicJson.actions, output)
  commandValues(logicJson && logicJson.postCommands, output)
  return output
}

function optionsOf(field = {}) {
  return new Set(asArray(field.datasource || field.options).map((option) => norm(option && (option.value ?? option.text ?? option.label))).filter(Boolean))
}

function navigationTargetsOf(data = {}) {
  const output = new Set()
  const visit = (value) => {
    if (value == null) return
    if (Array.isArray(value)) return value.forEach(visit)
    if (typeof value === 'string') {
      const target = norm(value)
      if (target) output.add(target)
      return
    }
    if (typeof value !== 'object') return
    ;['target', 'targetForm', 'subflow', 'entry_label', 'next_target', 'startForm'].forEach((key) => {
      const target = norm(value[key])
      if (target) output.add(target)
    })
    if (value.targets) visit(value.targets)
  }
  fieldsOf(data).forEach((field) => visit(field && field.source_additional && field.source_additional.targets))
  visit(data.actions)
  visit(data.menu_control && data.menu_control.options)
  visit(data.menu_options)
  visit(data.subflows)
  return output
}

function recall(expected, actual) {
  if (!expected.size) return 1
  let matches = 0
  expected.forEach((item) => { if (actual.has(item)) matches += 1 })
  return matches / expected.size
}

function executable(data = {}) {
  return valueFieldsOf(data).length > 0 || commandsOf(data).size > 0 || asArray(data.actions).length > 0
}

export function evaluateAgentGeneration(referenceData, agentData, options = {}) {
  const issues = []
  const addIssue = (code, severity, message, detail = {}) => issues.push({ code, severity, message, detail })
  const referenceFields = valueFieldsOf(referenceData)
  const agentFields = valueFieldsOf(agentData)
  const agentAliasSet = new Set(agentFields.flatMap(fieldAliases))
  const expectedCommands = commandsOf(referenceData, options.referenceLogicJson)
  const actualCommands = commandsOf(agentData, options.agentLogicJson)
  const expectedTargets = navigationTargetsOf(referenceData)
  const actualTargets = navigationTargetsOf(agentData)

  if (!executable(agentData)) {
    addIssue('AGENT_EVAL_NOT_EXECUTABLE', 'error', 'El resultado del agente no contiene campos, comandos ni acciones ejecutables.')
  }

  const missingFields = referenceFields.filter((field) => !fieldAliases(field).some((alias) => agentAliasSet.has(alias)))
  const missingFieldGroups = new Map()
  missingFields.forEach((field) => {
    const label = field.label || field.campoForm || field.tag || field.id || '(sin identificador)'
    const key = norm(label) || String(label)
    const current = missingFieldGroups.get(key) || { label, count: 0 }
    current.count += 1
    missingFieldGroups.set(key, current)
  })
  missingFieldGroups.forEach(({ label, count }) => addIssue(
    'AGENT_EVAL_MISSING_FIELD',
    'error',
    `Falta el campo de referencia: ${label}${count > 1 ? ` (${count} apariciones)` : ''}.`,
    { count },
  ))

  const missingCommands = Array.from(expectedCommands).filter((command) => !actualCommands.has(command))
  missingCommands.forEach((command) => addIssue('AGENT_EVAL_MISSING_COMMAND', 'error', `Falta el comando de referencia: ${command}.`))

  let expectedCatalogOptions = 0
  let matchedCatalogOptions = 0
  const missingCatalogIssues = new Set()
  referenceFields.forEach((referenceField) => {
    const expectedOptions = optionsOf(referenceField)
    if (!expectedOptions.size) return
    const aliases = fieldAliases(referenceField)
    const agentField = agentFields.find((field) => fieldAliases(field).some((alias) => aliases.includes(alias)))
    const actualOptions = optionsOf(agentField)
    expectedOptions.forEach((option) => {
      expectedCatalogOptions += 1
      if (actualOptions.has(option)) matchedCatalogOptions += 1
      else missingCatalogIssues.add(`${referenceField.label || aliases[0]}|${option}`)
    })
  })
  missingCatalogIssues.forEach((item) => {
    const separator = item.indexOf('|')
    const label = item.slice(0, separator)
    const option = item.slice(separator + 1)
    addIssue('AGENT_EVAL_MISSING_CATALOG_OPTION', 'error', `Falta una opción del catálogo en ${label}: ${option}.`)
  })

  const missingTargets = Array.from(expectedTargets).filter((target) => !actualTargets.has(target))
  missingTargets.forEach((target) => addIssue('AGENT_EVAL_MISSING_NAVIGATION', 'error', `Falta el destino de navegación: ${target}.`))

  const lint = lintDataJson(agentData)
  const agentLintErrors = lintErrors(lint)
  agentLintErrors.forEach((diagnostic) => addIssue(
    'AGENT_EVAL_LINT_ERROR',
    'error',
    diagnostic.message,
    { lintCode: diagnostic.code },
  ))

  const fieldRecall = referenceFields.length ? (referenceFields.length - missingFields.length) / referenceFields.length : 1
  const commandRecall = recall(expectedCommands, actualCommands)
  const catalogRecall = expectedCatalogOptions ? matchedCatalogOptions / expectedCatalogOptions : 1
  const navigationRecall = recall(expectedTargets, actualTargets)
  const executionScore = executable(agentData) ? 1 : 0
  const lintScore = Math.max(0, 1 - agentLintErrors.length * 0.5)
  const score = Math.round(100 * (
    executionScore * 0.10 +
    fieldRecall * 0.25 +
    commandRecall * 0.30 +
    catalogRecall * 0.15 +
    navigationRecall * 0.10 +
    lintScore * 0.10
  ))

  return {
    pass: issues.every((issue) => issue.severity !== 'error') && score >= 85,
    score,
    metrics: {
      executable: Boolean(executionScore),
      fieldRecall,
      commandRecall,
      catalogRecall,
      navigationRecall,
      lintScore,
      expectedFields: referenceFields.length,
      expectedCommands: expectedCommands.size,
      expectedCatalogOptions,
      expectedNavigationTargets: expectedTargets.size,
      lintErrors: agentLintErrors.length,
    },
    issues,
  }
}

export function evaluateAgentInterpretation(referenceData, referenceLogicJson, agentIr) {
  const issues = []
  const addIssue = (code, message, detail = {}) => issues.push({ code, severity: 'error', message, detail })
  const referenceFields = valueFieldsOf(referenceData)
  const interpretedFields = interpretationFieldsOf(agentIr)
  const interpretedAliases = new Set(interpretedFields.flatMap(fieldAliases))
  const expectedCommands = commandsOf(referenceData, referenceLogicJson)
  const interpretedCommands = commandsOf(agentIr)
  const expectedTargets = navigationTargetsOf(referenceData)
  const interpretedTargets = navigationTargetsOf(agentIr)

  const missingFields = referenceFields.filter((field) => !fieldAliases(field).some((alias) => interpretedAliases.has(alias)))
  const missingFieldGroups = new Map()
  missingFields.forEach((field) => {
    const label = field.label || field.campoForm || field.tag || field.id || '(sin identificador)'
    const key = norm(label) || String(label)
    const current = missingFieldGroups.get(key) || { label, count: 0 }
    current.count += 1
    missingFieldGroups.set(key, current)
  })
  missingFieldGroups.forEach(({ label, count }) => addIssue(
    'AGENT1_MISSING_FIELD',
    `El IR del Agente 1 omitió el campo: ${label}${count > 1 ? ` (${count} apariciones)` : ''}.`,
    { count },
  ))

  const missingCommands = Array.from(expectedCommands).filter((command) => !interpretedCommands.has(command))
  missingCommands.forEach((command) => addIssue('AGENT1_MISSING_COMMAND', `El IR del Agente 1 omitió el comando: ${command}.`))
  const missingTargets = Array.from(expectedTargets).filter((target) => !interpretedTargets.has(target))
  missingTargets.forEach((target) => addIssue('AGENT1_MISSING_NAVIGATION', `El IR del Agente 1 omitió el destino: ${target}.`))

  const fieldRecall = referenceFields.length ? (referenceFields.length - missingFields.length) / referenceFields.length : 1
  const commandRecall = recall(expectedCommands, interpretedCommands)
  const navigationRecall = recall(expectedTargets, interpretedTargets)
  const score = Math.round(100 * (fieldRecall * 0.35 + commandRecall * 0.50 + navigationRecall * 0.15))
  return {
    pass: issues.length === 0 && score >= 85,
    score,
    metrics: {
      fieldRecall,
      commandRecall,
      navigationRecall,
      expectedFields: referenceFields.length,
      expectedCommands: expectedCommands.size,
      expectedNavigationTargets: expectedTargets.size,
    },
    issues,
  }
}

